 
import { HttpInterceptorFn } from '@angular/common/http';
import { environment } from '@environment/environment';
import { AuthService } from '@services/auth/auth.service';
import { catchError, mergeMap } from 'rxjs';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { Router } from '@angular/router';

const baseUrl = environment.apiUrl;
const basePathname = new URL(baseUrl).pathname.replace(/\/$/, '');
const ckkGetParamName = 'is_ckk';
const ckkGetPaths = [
  '/expenses',
  '/expense_items',
  '/eb_ocean_freights',
  '/eb_ocean_freight_items',
  '/eb_tax_frees',
  '/eb_tax_free_items',
  '/import_brokers',
  '/import_broker_items',
  '/eb_product_prices',
  '/eb_product_price_items',
  '/eb_combineds',
  '/eb_combined_items',
  '/eb_initial_warehouses',
  '/eb_initial_warehouse_items',
  '/eb_drayages',
  '/eb_drayage_items',
  '/sale_orders',
  '/sale_order_items',
  '/sale_invoices',
  '/sale_invoice_items',
];

const excludedCkkGetActions = new Set(['cancel', 'reject', 'change_status', 'set_active', 'set_inactive']);

function getApiPathname(pathname: string): string {
  if (!basePathname || basePathname === '/') return pathname;

  return pathname === basePathname || pathname.startsWith(`${basePathname}/`) ? pathname.slice(basePathname.length) || '/' : pathname;
}

function shouldAddCkkParam(method: string, url: string, hasCkkHttpParam: boolean): boolean {
  if (method !== 'GET' || hasCkkHttpParam) return false;

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(url);
  } catch {
    return false;
  }

  if (parsedUrl.searchParams.has(ckkGetParamName)) return false;

  const pathname = getApiPathname(parsedUrl.pathname);
  const matchedPath = ckkGetPaths.find((path) => pathname === path || pathname.startsWith(`${path}/`));
  if (!matchedPath) return false;

  const nextSegment = pathname.slice(matchedPath.length).split('/').filter(Boolean)[0];
  return !nextSegment || !excludedCkkGetActions.has(nextSegment);
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // if (req.url.includes('https://')) {
  //   return next(req);
  // }

  // 1) ต่อ baseUrl ให้เฉพาะ request ที่เป็น path (ไม่ใช่ https://...)
  let apiReq = req;
  if (!req.url.startsWith('http')) {
    apiReq = req.clone({
      url: baseUrl + req.url,
    });
  }

  if (shouldAddCkkParam(apiReq.method, apiReq.urlWithParams, apiReq.params.has(ckkGetParamName))) {
    apiReq = apiReq.clone({
      setParams: {
        [ckkGetParamName]: 'true',
      },
    });
  }

  const isRefreshRequest = req.url.includes('/auth/refresh-token');
  const isVerifyRequest = req.url.includes('/auth/verify-token');
  const isLoginRequest = apiReq.url.includes('/auth/login');

  // 2) ดึง token จากที่เก็บ (localStorage / service)
  const token = authService.getToken();

  // 3) ถ้ามี token และไม่ใช่ login/refresh → ใส่ Authorization ให้อัตโนมัติ
  if (token && !isLoginRequest && !isRefreshRequest) {
    apiReq = apiReq.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // 4) handle 401 → refresh token แล้ว retry
  return next(apiReq).pipe(
    catchError((error) => {
      if (error.status === 401 && !isRefreshRequest && !isLoginRequest) {
        return authService.refreshToken().pipe(
          mergeMap((newToken: any) => {
            // เก็บ token ใหม่
            authService.setToken(newToken);

            const retryReq = apiReq.clone({
              setHeaders: {
                Authorization: `Bearer ${newToken}`,
              },
            });

            return next(retryReq);
          }),
          catchError((refreshError) => {
            console.error('Refresh token failed', refreshError);
            authService.clearToken();
            void router.navigate(['/auth/login']);
            return throwError(() => refreshError);
          }),
        );
      }

      return throwError(() => error);
    }),
  );

  // const reqWithCredentials = req.clone({
  //   url: baseUrl + req.url,
  //   withCredentials: true,
  // });

  // return next(reqWithCredentials).pipe(
  //   catchError((error) => {
  //     if (error.status === 401 && !isRefreshRequest && !isVerifyRequest) {
  //       return authService.refreshToken().pipe(
  //         mergeMap(() => {
  //           return next(reqWithCredentials);
  //         }),
  //         catchError((refreshError) => {
  //           console.error('Refresh token failed', refreshError);
  //           void router.navigate(['/auth/login']);
  //           return throwError(() => refreshError);
  //         }),
  //       );
  //     }
  //     return throwError(() => error);
  //   }),
  // );
};
